import React, { useState } from "react";
import { Check, FileDown } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, Tooltip, CartesianGrid, ComposedChart, Bar, Line, XAxis, YAxis,
} from "recharts";
import { api, formatApiError, API } from "../../lib/api";
import { GBP, GBP2 } from "../../lib/format";
import { Field } from "./utils";

/** Financials tab — project build economics (CAPEX / OPEX / PPA) that drive
 *  the Investor PDF and the analytics rollups. The line-item quote builder
 *  and What-If scenario compare tools were removed by design: a project
 *  either has a direct CAPEX £ total or resolves it from per-kWp × system
 *  size, and both can be edited here or on the Overview inline card. */
export default function QuoteTab({ project, design, quote, onSaved }) {
  // ---------- Project-level pricing (persisted on the Project) ----------
  const [capexTotal, setCapexTotal] = useState(
    project?.capex_gbp != null ? project.capex_gbp : ""
  );
  const [capexPerKwp, setCapexPerKwp] = useState(project?.capex_per_kwp_gbp ?? 850);
  const [opexPerKwp, setOpexPerKwp] = useState(project?.opex_per_kwp_gbp ?? 12);
  const [pricingBusy, setPricingBusy] = useState(false);

  // ---------- CAPEX vs Actual (variance tracker) ----------
  const [actualCapex, setActualCapex] = useState(
    project?.actual_capex_gbp != null ? String(project.actual_capex_gbp) : ""
  );
  const [actualBusy, setActualBusy] = useState(false);

  // ---------- Quote-level assumptions (persisted on latest Quote) ----------
  const [scP, setScP] = useState(quote?.financials?.self_consumption_pct ?? 45);
  const [importP, setImportP] = useState(quote?.financials?.import_tariff_p_per_kwh ?? 28.5);
  const [segP, setSegP] = useState(quote?.financials?.seg_tariff_p_per_kwh ?? 15);
  const [inflP, setInflP] = useState(quote?.financials?.inflation_pct ?? 3.5);
  const [degP, setDegP] = useState(quote?.financials?.degradation_pct ?? 0.5);
  const [discP, setDiscP] = useState(quote?.financials?.discount_rate_pct ?? 6);
  const [ppaRate, setPpaRate] = useState(quote?.ppa_rate_p_per_kwh ?? 18);
  const [gridRate, setGridRate] = useState(quote?.grid_rate_p_per_kwh ?? 30);
  const [ppaTerm, setPpaTerm] = useState(quote?.ppa_term_years ?? 25);
  const [escalator, setEscalator] = useState(quote?.annual_escalator_pct ?? 3);
  const [assumpBusy, setAssumpBusy] = useState(false);

  const sysKwp = Number(design?.systemSize || 0);
  const capexTotalNum = capexTotal === "" ? null : Number(capexTotal);
  const resolvedCapex = capexTotalNum != null && capexTotalNum > 0
    ? capexTotalNum
    : sysKwp * Number(capexPerKwp || 0);
  const capexSource = capexTotalNum != null && capexTotalNum > 0 ? "direct" : "per_kwp";
  const projectOpex = sysKwp * Number(opexPerKwp || 0);

  // Variance = actual - estimate. Positive = overrun, negative = underrun.
  const actualCapexNum = actualCapex === "" ? null : Number(actualCapex);
  const varianceGbp = actualCapexNum != null && resolvedCapex > 0
    ? actualCapexNum - resolvedCapex : null;
  const variancePct = varianceGbp != null && resolvedCapex > 0
    ? (varianceGbp / resolvedCapex) * 100 : null;
  const varianceTone = variancePct == null ? "neutral"
    : Math.abs(variancePct) < 2 ? "on_target"
    : variancePct > 0 ? "over" : "under";
  const varianceStyle = {
    on_target: { bg: "#DCFCE7", fg: "#166534" },
    over:      { bg: "#FEE2E2", fg: "#991B1B" },
    under:     { bg: "#DBEAFE", fg: "#1E40AF" },
    neutral:   { bg: "#F4F4F5", fg: "#52525B" },
  }[varianceTone];

  const saveActual = async () => {
    setActualBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, {
        actual_capex_gbp: actualCapex === "" ? null : Number(actualCapex),
      });
      toast.success("Actual CAPEX saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setActualBusy(false); }
  };

  const savePricing = async () => {
    setPricingBusy(true);
    try {
      await api.patch(`/projects/${project.id}`, {
        capex_gbp: capexTotal === "" ? null : Number(capexTotal),
        capex_per_kwp_gbp: Number(capexPerKwp),
        opex_per_kwp_gbp: Number(opexPerKwp),
      });
      toast.success("Pricing saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPricingBusy(false); }
  };

  const clearPricing = async () => {
    setPricingBusy(true);
    try {
      await api.post(`/projects/${project.id}/clear-pricing-override`);
      setCapexTotal("");
      setCapexPerKwp(850);
      setOpexPerKwp(12);
      toast.success("Reverted to global defaults");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setPricingBusy(false); }
  };

  const hasPricingOverride = project?.capex_gbp != null
    || project?.capex_per_kwp_gbp != null
    || project?.opex_per_kwp_gbp != null;

  const saveAssumptions = async () => {
    setAssumpBusy(true);
    try {
      await api.post("/quotes", {
        project_id: project.id,
        // Preserve any existing line items so the /quotes/{id}/pdf endpoint
        // (unchanged, still callable externally) stays functional.
        line_items: quote?.line_items ?? [],
        vat_pct: quote?.vat_pct ?? 20,
        notes: quote?.notes ?? "",
        self_consumption_pct: Number(scP),
        import_tariff_p_per_kwh: Number(importP),
        seg_tariff_p_per_kwh: Number(segP),
        inflation_pct: Number(inflP),
        discount_rate_pct: Number(discP),
        degradation_pct: Number(degP),
        ppa_rate_p_per_kwh: Number(ppaRate),
        grid_rate_p_per_kwh: Number(gridRate),
        ppa_term_years: Number(ppaTerm),
        annual_escalator_pct: Number(escalator),
      });
      toast.success("Financial assumptions saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAssumpBusy(false); }
  };

  const downloadPdf = (variant) => {
    if (!quote) { toast.error("Save assumptions first"); return; }
    const qs = variant && variant !== "classic" ? `?variant=${variant}` : "";
    window.open(`${API}/quotes/${quote.id}/pdf${qs}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="quote-tab">
      {/* -------- Project financials (CAPEX / OPEX) -------- */}
      <section className="swiss-card-flat p-6" data-testid="project-pricing-section">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="label">Project financials · CAPEX / OPEX</div>
            <div className="font-display text-lg font-semibold mt-1">Build economics</div>
            <div className="text-xs muted mt-1">
              CAPEX resolves in priority order: <strong>direct £ total</strong> → <strong>£/kWp × system size</strong> ({sysKwp.toFixed(2)} kWp) → global default.
              OPEX is always modelled as <strong>£/kWp/yr × system size</strong>. Both feed the Investor PDF, Analytics rollups, and Investor Dashboard.
            </div>
          </div>
          <div className="flex gap-2">
            {hasPricingOverride && (
              <button onClick={clearPricing} disabled={pricingBusy} className="btn-ghost"
                      data-testid="clear-pricing-btn"
                      title="Drop the per-project override and inherit from global Settings defaults">
                Use global default
              </button>
            )}
            <button onClick={savePricing} disabled={pricingBusy} className="btn-primary" data-testid="save-pricing-btn">
              <Check size={14} /> {pricingBusy ? "Saving…" : "Save pricing"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Field label="CAPEX total (£) — direct override" testid="f-capex-total">
            <input className="input-swiss" type="number" step="1" placeholder="Leave blank to use £/kWp"
                   value={capexTotal}
                   onChange={(e)=>setCapexTotal(e.target.value)} data-testid="capex-total-input" />
          </Field>
          <Field label="CAPEX (£ / kWp DC)" testid="f-capex-per-kwp">
            <input className="input-swiss" type="number" step="1" value={capexPerKwp}
                   onChange={(e)=>setCapexPerKwp(e.target.value)} data-testid="capex-per-kwp-input" />
          </Field>
          <Field label="OPEX (£ / kWp / yr)" testid="f-opex-per-kwp">
            <input className="input-swiss" type="number" step="0.5" value={opexPerKwp}
                   onChange={(e)=>setOpexPerKwp(e.target.value)} data-testid="opex-per-kwp-input" />
          </Field>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
          <div className="px-3 py-2 border border-zinc-200 rounded-sm">
            <div className="label flex items-center gap-2">
              Total CAPEX
              <span className="text-[10px] muted font-mono">
                ({capexSource === "direct" ? "direct £" : "£/kWp × kWp"})
              </span>
            </div>
            <div className="font-mono mt-1 text-lg" data-testid="project-total-capex">
              {GBP.format(Math.max(0, resolvedCapex))}
            </div>
          </div>
          <div className="px-3 py-2 border border-zinc-200 rounded-sm">
            <div className="label">Annual OPEX</div>
            <div className="font-mono mt-1 text-lg" data-testid="project-annual-opex">
              {GBP.format(Math.max(0, projectOpex))}
            </div>
          </div>
        </div>
        <div className="text-[11px] muted mt-3">
          Stored on the project (not the quote). Leave the direct total blank to let it recompute automatically from £/kWp × system size whenever the design changes.
        </div>
      </section>

      {/* -------- CAPEX vs Actual (variance tracker) -------- */}
      <section className="swiss-card-flat p-6" data-testid="capex-variance-section">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="label">Build cost tracker</div>
            <div className="font-display text-lg font-semibold mt-1">CAPEX vs Actual</div>
            <div className="text-xs muted mt-1">
              Log the invoiced build cost once the project completes. Variance is highlighted so overruns / underruns surface without spreadsheets.
            </div>
          </div>
          <button onClick={saveActual} disabled={actualBusy} className="btn-primary" data-testid="save-actual-capex-btn">
            <Check size={14} /> {actualBusy ? "Saving…" : "Save actual"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="px-3 py-3 border border-zinc-200 rounded-sm" data-testid="capex-estimate-line">
            <div className="label">Estimate</div>
            <div className="font-mono mt-1 text-lg">{GBP.format(Math.max(0, resolvedCapex))}</div>
            <div className="text-[10px] muted mt-1">
              {capexSource === "direct" ? "Direct £ override" : `£${Number(capexPerKwp || 0)}/kWp × ${sysKwp.toFixed(2)} kWp`}
            </div>
          </div>
          <div className="px-3 py-3 border border-zinc-200 rounded-sm" data-testid="capex-actual-line">
            <div className="label">Actual (invoiced)</div>
            <input className="input-swiss mt-1 w-full" type="number" step="1"
                   placeholder="Enter invoiced build £"
                   value={actualCapex}
                   onChange={(e)=>setActualCapex(e.target.value)}
                   data-testid="actual-capex-input" />
            <div className="text-[10px] muted mt-1">Leave blank if not yet captured.</div>
          </div>
          <div className="px-3 py-3 border border-zinc-200 rounded-sm" data-testid="capex-variance-line">
            <div className="label">Variance</div>
            <div className="font-mono mt-1 text-lg" data-testid="capex-variance-gbp">
              {varianceGbp == null ? "—" : `${varianceGbp >= 0 ? "+" : "−"}${GBP.format(Math.abs(varianceGbp))}`}
            </div>
            <div className="mt-1">
              {variancePct == null ? (
                <span className="text-[11px] muted">No actual yet</span>
              ) : (
                <span
                  className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded"
                  style={{ background: varianceStyle.bg, color: varianceStyle.fg }}
                  data-testid="capex-variance-badge"
                  data-tone={varianceTone}
                >
                  {varianceTone === "on_target" ? "On target"
                    : varianceTone === "over" ? `↑ Over budget · ${variancePct.toFixed(1)}%`
                    : `↓ Under budget · ${Math.abs(variancePct).toFixed(1)}%`}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-[11px] muted mt-3">
          Variance uses the resolved estimate (direct £ or £/kWp × system size). Within ±2% counts as on target.
        </div>
      </section>

      {/* -------- PPA / dual financial model inputs -------- */}
      <section className="swiss-card-flat p-6" data-testid="ppa-section">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="label">Dual financial model · PPA inputs</div>
            <div className="font-display text-lg font-semibold mt-1">Customer PPA vs Grid</div>
            <div className="text-xs muted mt-1">Drives the Investor PDF (CapEx · IRR · NPV) and the Customer PDF (savings teaser, no CapEx).</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {quote && (
              <button onClick={() => downloadPdf("investor")} className="btn-ghost" data-testid="download-investor-pdf-btn" title="Internal investor report — CapEx, IRR, NPV, 25-yr cashflow">
                <FileDown size={13} /> Investor PDF
              </button>
            )}
            {quote && (
              <button onClick={() => downloadPdf("customer")} className="btn-ghost" data-testid="download-customer-pdf-btn" title="Customer-facing PPA savings PDF — no CapEx/IRR">
                <FileDown size={13} /> Customer PDF
              </button>
            )}
            <button onClick={saveAssumptions} disabled={assumpBusy} className="btn-primary" data-testid="save-assumptions-btn">
              <Check size={14} /> {assumpBusy ? "Saving…" : "Save assumptions"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <Field label="PPA rate (p/kWh)" testid="f-ppa">
            <input className="input-swiss" type="number" step="0.1" value={ppaRate}
                   onChange={(e)=>setPpaRate(e.target.value)} data-testid="ppa-rate-input" />
          </Field>
          <Field label="Grid rate (p/kWh)" testid="f-grid">
            <input className="input-swiss" type="number" step="0.1" value={gridRate}
                   onChange={(e)=>setGridRate(e.target.value)} data-testid="grid-rate-input" />
          </Field>
          <Field label="Term (years)" testid="f-term">
            <input className="input-swiss" type="number" value={ppaTerm}
                   onChange={(e)=>setPpaTerm(e.target.value)} data-testid="ppa-term-input" />
          </Field>
          <Field label="Annual escalator %" testid="f-esc">
            <input className="input-swiss" type="number" step="0.1" value={escalator}
                   onChange={(e)=>setEscalator(e.target.value)} data-testid="ppa-escalator-input" />
          </Field>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="px-3 py-2 border border-zinc-200 rounded-sm">
            <div className="label">Monthly savings (est)</div>
            <div className="font-mono mt-1" data-testid="ppa-monthly-savings">
              {(() => {
                const ann = design?.annualEnergyProduction || 0;
                const mKwh = ann / 12;
                const s = (mKwh * (Number(gridRate) - Number(ppaRate))) / 100;
                return GBP2.format(Math.max(0, s));
              })()}
            </div>
          </div>
          <div className="px-3 py-2 border border-zinc-200 rounded-sm">
            <div className="label">Year-1 savings (est)</div>
            <div className="font-mono mt-1" data-testid="ppa-year1-savings">
              {(() => {
                const ann = design?.annualEnergyProduction || 0;
                const s = (ann * (Number(gridRate) - Number(ppaRate))) / 100;
                return GBP.format(Math.max(0, s));
              })()}
            </div>
          </div>
          <div className="px-3 py-2 border border-zinc-200 rounded-sm">
            <div className="label">{ppaTerm}-year total (est)</div>
            <div className="font-mono mt-1" data-testid="ppa-lifetime-savings">
              {(() => {
                const ann = design?.annualEnergyProduction || 0;
                const s = (ann * (Number(gridRate) - Number(ppaRate))) / 100 * Number(ppaTerm);
                return GBP.format(Math.max(0, s));
              })()}
            </div>
          </div>
          <div className="px-3 py-2 border border-zinc-200 rounded-sm">
            <div className="label">PPA discount vs grid</div>
            <div className="font-mono mt-1" data-testid="ppa-discount">
              {gridRate > 0 ? `${(((Number(gridRate) - Number(ppaRate)) / Number(gridRate)) * 100).toFixed(1)}%` : "—"}
            </div>
          </div>
        </div>
        <div className="text-[11px] muted mt-3">
          Quick preview only — the PDFs use a full degradation-adjusted, escalator-compounded model.
        </div>
      </section>

      {/* -------- UK financial assumptions -------- */}
      <section className="swiss-card-flat p-6">
        <div className="label mb-4">UK financial assumptions (20-year model)</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
          <Field label="Self-consumption %" testid="f-sc"><input className="input-swiss" type="number" value={scP} onChange={(e)=>setScP(e.target.value)} /></Field>
          <Field label="Import tariff (p/kWh)" testid="f-import"><input className="input-swiss" type="number" step="0.1" value={importP} onChange={(e)=>setImportP(e.target.value)} /></Field>
          <Field label="SEG export (p/kWh)" testid="f-seg"><input className="input-swiss" type="number" step="0.1" value={segP} onChange={(e)=>setSegP(e.target.value)} /></Field>
          <Field label="Energy inflation %" testid="f-infl"><input className="input-swiss" type="number" step="0.1" value={inflP} onChange={(e)=>setInflP(e.target.value)} /></Field>
          <Field label="Panel degradation %/yr" testid="f-deg"><input className="input-swiss" type="number" step="0.05" value={degP} onChange={(e)=>setDegP(e.target.value)} /></Field>
          <Field label="Discount rate %" testid="f-disc"><input className="input-swiss" type="number" step="0.1" value={discP} onChange={(e)=>setDiscP(e.target.value)} /></Field>
        </div>
      </section>

      {/* -------- 20-year cashflow chart -------- */}
      {quote?.financials?.cashflow?.length > 0 && (
        <section className="swiss-card-flat p-6">
          <div className="flex flex-wrap items-end justify-between mb-4 gap-3">
            <div>
              <div className="label">Financial model</div>
              <div className="font-display text-lg font-semibold mt-1">20-year cashflow projection</div>
            </div>
            <div className="flex gap-6 text-sm">
              <div><div className="label">Payback</div><div className="font-mono">{quote.financials.payback_years} yr</div></div>
              <div><div className="label">IRR</div><div className="font-mono">{quote.financials.irr_pct?.toFixed?.(2)}%</div></div>
              <div><div className="label">NPV</div><div className="font-mono">{GBP.format(quote.financials.npv_gbp)}</div></div>
              <div><div className="label">Lifetime savings</div><div className="font-mono">{GBP.format(quote.financials.lifetime_savings_gbp)}</div></div>
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
              <ComposedChart data={quote.financials.cashflow}>
                <CartesianGrid stroke="#E4E4E7" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                <Tooltip contentStyle={{ borderRadius: 0, fontSize: 12 }} formatter={(v, n) => [GBP2.format(v), n === "savings_gbp" ? "Annual savings" : "Cumulative"]} />
                <Bar dataKey="savings_gbp" fill="#F59E0B" />
                <Line dataKey="cumulative_gbp" stroke="#0A0A0A" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}
    </div>
  );
}
